class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    // helper variables
    this.cartTotalValue = 0;
    this.cartTotalDiscount = 0;
    this.cartOriginalPrice = 0;

    // elements
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    //  Store max quantities for validation
    this.maxQuantities = {};

    // events
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // Init items from API to prevent cart content missmatch on browser back/forward commands
    this.renderItems();

    // dev events
    document.addEventListener('ajaxProduct:added', () => {
      this.renderAndShowItems()
    })

    // Free shipping check onload
    this.updateShippingNotification('cart-shipping-notification');
    
    //  Initialize max quantities
    this.initializeMaxQuantities();
  }

  /**
   *  Initialize max quantities from cart items
   */
  async initializeMaxQuantities() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      if (!cartData || !cartData.items) return;
      
      for (const item of cartData.items) {
        const lineIndex = cartData.items.indexOf(item) + 1;
        const properties = item.properties || {};
        
        // Skip if no inventory properties
        if (!properties._inventory_qty) continue;

        const inventoryQty = parseInt(properties._inventory_qty) || 0;

        // Set max quantity based on available inventory
        if (inventoryQty > 0) {
          this.maxQuantities[lineIndex] = inventoryQty;
          this.setMaxAttribute(lineIndex, inventoryQty);
        }
      }
    } catch (error) {
      console.error('Error initializing max quantities:', error);
    }
  }

  /**
   *  Set max attribute on quantity input
   */
  setMaxAttribute(lineIndex, maxQty) {
    const cartItem = document.getElementById(`CartItem-${lineIndex}`);
    if (!cartItem) return;

    const quantityInput = cartItem.querySelector('input[name="updates[]"]');
    if (quantityInput) {
      quantityInput.setAttribute('max', maxQty);
      quantityInput.setAttribute('data-max', maxQty);
    }
  }

  /**
   *  Show error message
   */
  showErrorMessage(lineIndex, message) {
    const cartItem = document.getElementById(`CartItem-${lineIndex}`);
    if (!cartItem) return;

    const errorContainer = cartItem.querySelector('.cart-item__error');
    const errorText = cartItem.querySelector('.cart-item__error-text');

    if (!errorContainer || !errorText) return;

    errorText.innerHTML = message;
    errorContainer.classList.remove('hide');
  }

  /**
   *  Clear error message
   */
  clearErrorMessage(lineIndex) {
    const cartItem = document.getElementById(`CartItem-${lineIndex}`);
    if (!cartItem) return;

    const errorContainer = cartItem.querySelector('.cart-item__error');
    const errorText = cartItem.querySelector('.cart-item__error-text');

    if (!errorContainer || !errorText) return;

    errorText.innerHTML = '';
    errorContainer.classList.add('hide');
  }

  /**
   *  Debounced onChange event handler. Updates the cart when the user stops clicking quantity buttons.
   * @param {*} event
   */
  onChange(event) {
    const input = event.target;
    const lineIndex = event.target.dataset.index;
    
    //  Validate against max quantity if exists
    if (this.maxQuantities[lineIndex]) {
      const maxQty = this.maxQuantities[lineIndex];
      const requestedQty = parseInt(input.value) || 0;
      
      if (requestedQty > maxQty) {
        // Show error message
        this.showErrorMessage(
          lineIndex, 
          `Only ${maxQty} item${maxQty > 1 ? 's are' : ' is'} available due to stock limitations.`
        );
        
        // Reset input to max quantity
        input.value = maxQty;
        
        // STOP - Don't update cart to prevent glitch
        return;
      } else {
        // Clear error if quantity is valid
        this.clearErrorMessage(lineIndex);
      }
    }
    
    // Continue with normal update
    this.updateQuantity(lineIndex, input.value, document.activeElement.getAttribute('name'));
  }

  /**
   * Returns an array of sections to render when the cart is updated. Limit: 5 sections.
   * Does not make the request to the server, only returns the section names to render.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Array} Array of sections to render
   */
  getSectionItemsToRender() {
    return ['cart-items','cart-shipping-notification', 'cart-recommendations', 'cart-note', 'cart-footer'];
  }

  /**
   * This function is always called when the cart is updated.
   * @param {string} json
   */
  updateCartSections (newHTML) {
    this.getSectionItemsToRender().forEach((item) => {
      const elementToReplace = document.getElementById(item);

      if (!elementToReplace) return;

      // replace old with new
      elementToReplace.innerHTML = newHTML.getElementById(item).innerHTML;

      if (item === 'cart-shipping-notification') {
        this.updateShippingNotification('cart-shipping-notification');
      }
      
      //  Re-initialize max quantities after cart update
      if (item === 'cart-items') {
        setTimeout(() => {
          this.initializeMaxQuantities();
        }, 200);
      }
    });
  }

  /**
   * This function handles multi-currency support for the shipping notification bar.
   * This is called when item is added to cart or when the cart is updated.
   * @param {string} selector
   * @param {string*} json - Cart JSON
   */
  updateShippingNotification(selector) {
    const shippingNotificationWrapper = document.getElementById(selector);

    if (!shippingNotificationWrapper) {
      return;
    }

    const shippingNotification = shippingNotificationWrapper.querySelector('[data-free-shipping-notification]');

    let cartTotal = parseInt(shippingNotification.dataset.cartTotal, 10),
        freeShippingTreshold = parseInt(shippingNotification.dataset.threshold,10),
        shopCurrency = shippingNotification.dataset.shopCurrency,
        targetCurrency = shippingNotification.dataset.targetCurrency;

    if (shopCurrency !== targetCurrency) {
      cartTotal = Math.round(cartTotal / (parseFloat(Shopify.currency.rate) || 1 ));
    }

    if (cartTotal > freeShippingTreshold) {
      shippingNotification.classList.add('threshold-reached')
      shippingNotification.querySelector('span').innerHTML = window.cartStrings.shipping_free;
    } else {
      let missingAmount = freeShippingTreshold - cartTotal,
          message = window.cartStrings.shipping_not_free.replace('[amount]', Woolman.Utils.formatMoney(missingAmount));

      shippingNotification.classList.remove('threshold-reached')
      shippingNotification.querySelector('span').innerHTML = message;
    }
  }

  /**
   * Fetches fresh sections from the server and updates the cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderItems() {
    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateCartSections(html);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Fetches fresh sections from the server and updates the cart. This is called when item is added to cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderAndShowItems() {
    const parentDrawer = this.closest('[data-drawer]');

    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateCartSections(html);

        if (!parentDrawer) return;
        Woolman.ModalsAndDrawers.showModalOrDrawer(parentDrawer.getAttribute('id'))
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Updates the quantity of a line item in the cart. This is called when the user changes the quantity of an item in the cart.
   * @param {*} line
   * @param {*} quantity
   * @param {*} name
   */
  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    // Address the line by its key, not its position. Positions go stale when
    // something else changes the cart after this page rendered (Shopify
    // checkout dropping a sold-out item, Redo re-adding its coverage line,
    // another tab), and a stale position either hits the wrong line or fails
    // with "line parameter is invalid".
    const key = this.querySelector(`#CartItem-${line}`)?.dataset.key;

    fetch(`${routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
      body: JSON.stringify({
        ...(key ? { id: key } : { line: line }),
        quantity: quantity,
        sections: 'main-cart',
        sections_url: window.location.pathname,
      }),
    })
    .then((response) => response.json())
    .then((responseJson) => {

      const quantityElement = document.getElementById(`Quantity-${line}`);
      const items = document.querySelectorAll('.cart-item');

      // The line is no longer in the cart (or the page is out of date):
      // show the cart as it is now instead of an error.
      if (!responseJson.sections && !responseJson.errors) {
        this.renderItems();
        document.dispatchEvent(new CustomEvent('cart:update'));
        return;
      }

      if (responseJson.errors) {
        quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, responseJson.errors);
        return;
      }

      const event = new CustomEvent('cart:update', { detail: responseJson,});
      document.dispatchEvent(event);

      // Render new items according to response
      const html = new DOMParser().parseFromString(responseJson.sections['main-cart'], 'text/html');
      this.updateCartSections(html);

      const lineItem = document.getElementById(`CartItem-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) lineItem.querySelector(`[name="${name}"]`).focus();
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      this.disableLoading();
    });
  }

  /**
   * Updates the live regions for the cart.
   * @param {*} line
   * @param {*} message
   */
  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      lineItemError.classList.remove('hide');
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    this.classList.add('is-processing');
    this.querySelectorAll(`#CartItem-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
  }

  disableLoading() {
    this.classList.remove('is-processing');
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
           'Content-Type': 'application/json',
            Accept: `application/json`,
          },
          ...{ body }
        });
      }, 300))
    }
  });
};

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loadRecommendations();
      document.addEventListener('ajaxProduct:added', this.loadRecommendations.bind(this))
    }

    async loadRecommendations() {
      if (!this.dataset.productId || this.dataset.productId === '') return;

      try {
        const url = new URL(location.origin + routes.product_recommendations_url + '.json')
        url.searchParams.set('product_id', this.dataset.productId)
        url.searchParams.set('limit', this.dataset.limit || 10)

        const res = await fetch(url)
        const { products } = await res.json();

        if (!products || products.length == 0) {
          throw 'No product recommendations found for products in the cart'
        }

        this.productsContainer = this.querySelector('[data-products-ajax]')
        this.productsContainer.innerHTML = '';

        products.forEach((product) => {

          const template = this.querySelector('#template-recommendation-item').content.firstElementChild.cloneNode(true);
          const filename = product.featured_image.split('/').pop();
          const lastDotIndex = filename.lastIndexOf('.');
          const newFilename = `${filename.substring(0, lastDotIndex)}_300x${filename.substring(lastDotIndex)}`;
          const url = product.featured_image.replace(filename, newFilename);

          template.querySelector('img').setAttribute('src', url);
          template.querySelector('.card-media').setAttribute('href', location.origin + product.url);
          template.setAttribute('title', product.title);
          template.querySelector('.card-title a').textContent = product.title;
          template.querySelector('.card-title a').setAttribute('href', location.origin + product.url);

          this.productsContainer.appendChild(template);
        })
      } catch(error) {
        this.setAttribute('hidden', true)
        console.warn(error)
      }
    }
  })
}

// Shared across instances: the theme editor re-instantiates the validator on
// every section load, and a per-instance Set let listeners stack on one form.
const boundProductForms = new WeakSet();
const inFlightProductForms = new WeakSet();

// iOS Safari throws the same "TypeError: Load failed" for a dropped connection
// and for a fetch cancelled by navigating away. Track unload to tell them apart.
let pageIsUnloading = false;
window.addEventListener('pagehide', () => { pageIsUnloading = true; });
window.addEventListener('pageshow', () => { pageIsUnloading = false; });

class ProductFormValidator {
  constructor() {
    this.forms = document.querySelectorAll('form[action*="/cart/add"]');
    this.init();
  }

  init() {
    this.forms.forEach(form => {
      if (boundProductForms.has(form)) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        // This listener pre-empts Taiga's product-form handler, so its
        // aria-disabled guard never runs. Without our own, a double tap sends
        // two adds: the shopper gets 2 in the cart, or a sold-out error on the
        // second request for an item that was in fact added.
        if (inFlightProductForms.has(form)) return;
        inFlightProductForms.add(form);
        this.setSubmitting(form, true);

        try {
          const canAdd = await this.validateBeforeAddToCart(form);

          if (canAdd) {
            await this.actuallySubmitForm(form);
          }
        } finally {
          inFlightProductForms.delete(form);
          this.setSubmitting(form, false);
        }
      }, { capture: true, once: false });

      boundProductForms.add(form);
    });
  }

  setSubmitting(form, isSubmitting) {
    const submitButton = form.querySelector('[type="submit"]');
    if (!submitButton) return;

    if (isSubmitting) {
      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');
    } else {
      submitButton.removeAttribute('aria-disabled');
      submitButton.classList.remove('loading');
    }
    form.closest('product-form')?.querySelector('.loading-overlay')?.classList.toggle('hidden', !isSubmitting);
  }

  postAddToCart(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
  }

  async getCartQuantity(variantId) {
    const cartResponse = await fetch('/cart.js');
    const cartData = await cartResponse.json();
    return cartData.items
      .filter(item => item.variant_id == variantId)
      .reduce((total, item) => total + item.quantity, 0);
  }

  // A network failure does not say whether the add reached Shopify, so a blind
  // retry could double-add. Compare the cart against the pre-add quantity:
  // if it grew, the add landed; only if it did not, send the request again.
  async recoverFromNetworkFailure(payload, variantId, qtyBefore) {
    await new Promise(resolve => setTimeout(resolve, 500));

    if (qtyBefore !== null) {
      const qtyNow = await this.getCartQuantity(variantId);
      if (qtyNow > qtyBefore) {
        return { landed: true };
      }
    }

    return { landed: false, response: await this.postAddToCart(payload) };
  }

  async actuallySubmitForm(form) {
    const formData = new FormData(form);
    const variantId = formData.get('id');

    try {
      const payload = {
        items: [{
          id: variantId,
          quantity: parseInt(formData.get('quantity') || 1),
          properties: this.extractProperties(formData)
        }]
      };

      let qtyBefore = null;
      try {
        qtyBefore = await this.getCartQuantity(variantId);
      } catch (error) {
        // Unknown baseline: recovery falls back to a plain single retry.
      }

      let response;
      let retried = false;
      try {
        response = await this.postAddToCart(payload);
      } catch (networkError) {
        if (pageIsUnloading) throw networkError;

        retried = true;
        const recovery = await this.recoverFromNetworkFailure(payload, variantId, qtyBefore);
        if (recovery.landed) {
          this.logCartError('recovered', {
            reason: 'network failure, add had landed',
            error: networkError.message,
            variantId,
            url: window.location.href
          });
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: {}, bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:update', { detail: {}, bubbles: true }));
          this.hideError(form);
          return;
        }
        response = recovery.response;
      }

      const data = await response.json();

      if (response.ok && retried) {
        this.logCartError('recovered', {
          reason: 'network failure, retry succeeded',
          variantId,
          url: window.location.href
        });
      }

      if (response.ok) {
        const event = new CustomEvent('ajaxProduct:added', {
          detail: data,
          bubbles: true
        });
        document.dispatchEvent(event);

        const cartUpdateEvent = new CustomEvent('cart:update', {
          detail: data,
          bubbles: true
        });
        document.dispatchEvent(cartUpdateEvent);

        this.hideError(form);
      } else {
        // Shopify says "already sold out" for a variant it will not sell to the
        // shopper's country. When the PDP believed it sellable and the session
        // is not US, that is a shipping restriction; say so instead.
        const pdpBelievedSellable = parseInt(formData.get('properties[_inventory_qty]') || 0) > 0
          || formData.get('properties[_inventory_policy]') === 'continue';
        const country = window.Shopify && window.Shopify.country;
        const marketRestricted = response.status === 422 && pdpBelievedSellable && country && country !== 'US';
        const notShippable = window.variantStrings && window.variantStrings.inventory && window.variantStrings.inventory.not_shippable;
        this.showError(form, (marketRestricted && notShippable) || data.description || 'Unable to add item to cart.');
        this.logCartError('shopify_rejected', {
          marketRestricted,
          status: response.status,
          description: data.description,
          message: data.message,
          variantId: formData.get('id'),
          quantity: formData.get('quantity'),
          url: window.location.href
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const aborted = pageIsUnloading;
      // A request cancelled by leaving the page is not a failure the shopper
      // can act on; an error banner would greet them on back-navigation.
      if (!aborted) {
        this.showError(form, 'We could not reach the cart. Please check your connection and try again.');
      }
      this.logCartError('exception', {
        error: error.message,
        aborted,
        variantId,
        url: window.location.href
      });
    }
  }

  extractProperties(formData) {
    const properties = {};
    
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        properties[propName] = value;
      }
    }
    
    return properties;
  }

  async validateBeforeAddToCart(form) {
    try {
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const requestedQty = parseInt(formData.get('quantity') || 1);
      
      const inventoryQty = parseInt(formData.get('properties[_inventory_qty]') || 0);
      const inventoryPolicy = formData.get('properties[_inventory_policy]') || 'deny';

      const productTags = window.productTags || [];
      const hasPreorderTag = productTags.includes('preorder');
      const hasCustomOrderTag = productTags.includes('custom-order');

      let maxAllowedQty = null;

      // Preorder/custom-order products: allow if policy is continue
      if (hasPreorderTag || hasCustomOrderTag) {
        if (inventoryQty > 0) {
          maxAllowedQty = inventoryQty;
        } else if (inventoryPolicy === 'continue') {
          this.hideError(form);
          return true;
        } else {
          this.showError(form, 'This item is currently out of stock.');
          this.logCartError('validation_out_of_stock', {
            reason: 'preorder/custom-order with deny policy and 0 qty',
            variantId, inventoryQty, inventoryPolicy,
            tags: productTags.filter(t => ['preorder','custom-order'].includes(t)),
            url: window.location.href
          });
          return false;
        }
      }
      // Regular products
      else {
        if (inventoryQty > 0) {
          maxAllowedQty = inventoryQty;
        } else if (inventoryPolicy === 'continue') {
          this.hideError(form);
          return true;
        } else {
          this.showError(form, 'This item is currently out of stock.');
          this.logCartError('validation_out_of_stock', {
            reason: 'regular product with deny policy and 0 qty',
            variantId, inventoryQty, inventoryPolicy,
            url: window.location.href
          });
          return false;
        }
      }

      // If no limit is set, allow the add
      if (maxAllowedQty === null) {
        this.hideError(form);
        return true;
      }

      // Check current cart quantity
      const currentQtyInCart = await this.getCartQuantity(variantId);

      const totalAfterAdd = currentQtyInCart + requestedQty;

      // Validate total quantity
      if (totalAfterAdd > maxAllowedQty) {
        const remaining = maxAllowedQty - currentQtyInCart;
        
        if (remaining <= 0) {
          this.showError(form, `Maximum quantity (${maxAllowedQty}) of this item is already in your cart.`);
        } else {
          this.showError(form, `You can only add ${remaining} more of this item. Maximum total: ${maxAllowedQty}.`);
        }
        
        return false;
      }

      // Validation passed
      this.hideError(form);
      return true;

    } catch (error) {
      // The pre-check is a courtesy; Shopify enforces the real limit on add.
      // Failing closed here left the button doing nothing, with no message.
      console.error('Validation error:', error);
      return true;
    }
  }

  showError(form, message) {
    let errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    
    if (!errorWrapper) {
      errorWrapper = form.closest('.product-form')?.querySelector('.product-form__error-message-wrapper');
    }

    if (!errorWrapper) {
      errorWrapper = form.querySelector('[data-error-message]');
      if (!errorWrapper) {
        errorWrapper = document.querySelector('.product-form__error-message-wrapper');
      }
    }

    if (!errorWrapper) {
      errorWrapper = document.createElement('div');
      errorWrapper.className = 'product-form__error-message-wrapper';
      errorWrapper.innerHTML = `
        <div class="product-form__error-message">
          <span class="svg-wrapper">
            <svg aria-hidden="true" focusable="false">
              <use href="#icon-error" />
            </svg>
          </span>
          <span class="product-form__error-message-text"></span>
        </div>
      `;
      
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.parentNode.insertBefore(errorWrapper, submitBtn);
      } else {
        form.appendChild(errorWrapper);
      }
    }

    const errorMessageText = errorWrapper.querySelector('.product-form__error-message-text') || 
                            errorWrapper.querySelector('.product-form__error-message');
    
    if (errorMessageText) {
      errorMessageText.textContent = message;
    } else {
      errorWrapper.textContent = message;
    }

    errorWrapper.removeAttribute('hidden');
    errorWrapper.classList.remove('hidden');
    errorWrapper.style.display = 'block';
  }

  hideError(form) {
    let errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    
    if (!errorWrapper) {
      errorWrapper = form.closest('.product-form')?.querySelector('.product-form__error-message-wrapper');
    }

    if (!errorWrapper) {
      errorWrapper = form.querySelector('[data-error-message]');
    }

    if (errorWrapper) {
      errorWrapper.setAttribute('hidden', '');
      errorWrapper.classList.add('hidden');
      errorWrapper.style.display = 'none';
    }
  }

  logCartError(type, details) {
    const payload = {
      type,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      ...details
    };
    console.warn('[Cart Error]', type, payload);

    // Push to GTM dataLayer for GA4 tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cart_error',
      cart_error_type: type,
      cart_error_variant: details.variantId || '',
      cart_error_reason: details.reason || details.description || details.error || '',
      cart_error_aborted: details.aborted === true,
      cart_error_url: details.url || window.location.href
    });

    // Fire custom event for any other listeners
    document.dispatchEvent(new CustomEvent('cart:error', { detail: payload }));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductFormValidator();
  });
} else {
  new ProductFormValidator();
}

document.addEventListener('shopify:section:load', () => {
  new ProductFormValidator();
});